import fullStoryPlugin from '@analytics/fullstory';
import googleAnalytics from '@analytics/google-analytics-v3';
import { Analytics, AnalyticsInstance } from 'analytics';
import { timeout } from 'promise-timeout';
// eslint-disable-next-line import/no-named-as-default
import mParticlePlugin from './analyticsMParticle';

let googleLinkerParam: string;
// eslint-disable-next-line import/no-mutable-exports
let analytics: AnalyticsInstance;
const GET_GA_TRACKER_LINKER_PARAM_TIMEOUT = 1000;
const LINKER_PARAM = 'linkerParam';
const FULLSTORY_PARAM = '_fs';
export interface PageView {
  Path: string;
  Url: string;
  Host: string;
  Title?: string;
}

export enum Theme {
  Advantage = 'advantage',
  Anz = 'anz',
  CmcInvestSingapore = 'cmcinvestsingapore',
  Bendigo = 'bendigo',
  Boq = 'boq',
  Cmc = 'cmc',
  Collateraliseportal = 'collateraliseportal',
  Ebroking = 'ebroking',
  Mola = 'mola',
  Rivkin = 'rivkin',
  StGeorge = 'stgeorge',
  Suncorp = 'suncorp',
  Supervision = 'supervision'
}

export interface UserAttributes {
  Theme?: Theme;
  ActivityStatus?: ActivityStatus;
  Persona?: Persona;
  PremiumDesk?: boolean;
  PremiumDeskManager?: string;
  Tenure?: number;
  Falcon?: boolean;
  displayName?: string; // Fullstory
}

export interface EventAttributes {
  AccountId?: string;
}

//mParticle.EventType
export enum EventType {
  Unknown = 0,
  Navigation = 1,
  Location = 2,
  Search = 3,
  Transaction = 4,
  UserContent = 5,
  UserPreference = 6,
  Social = 7,
  Other = 8,
  Media = 9
}
export interface EventOptions {
  type: EventType;
}

export enum ActivityStatus {
  Active = 'Active',
  Dormant = 'Dormant',
  Inactive = 'Inactive',
  NeverTraded = 'NeverTraded'
}

export enum Persona {
  Alexander = 'Alexander',
  Finn = 'Finn',
  Lachy = 'Lachy',
  Tyler = 'Tyler',
  Unknown = 'Unknown',
  William = 'William'
}
export interface IAnalyticsConfig {
  debug: boolean;
  fullStory: {
    isEnabled: boolean;
    organisationId: string;
  };
  braze: {
    isEnabled: boolean;
    apiKey: string;
    baseUrl: string;
    contentSecurityNonce: string;
  };
  mParticle: {
    isEnabled: boolean;
    apiKey: string;
    isDevelopmentMode: boolean;
    workspaceToken: string;
    deviceId?: string;
    logLevel?: 'verbose' | 'warning' | 'none' | undefined;
    dataPlan: {
      planId: string;
      planVersion: number;
    };
    customFlags: any;
    enabledPlugins: {
      googleAnalytics: boolean;
      googleTagManager: boolean;
      braze: boolean;
      amplitude: boolean;
      googleAnalytics4: boolean;
      bing: boolean;
      twitter: boolean;
      facebook: boolean;
      oneTrust: boolean;
      googleAds: boolean;
    };
  };
  googleAnalytics: {
    isEnabled: boolean;
    trackingId: string;
    debug: boolean;
    autoLinkDomains: string[];
  };
}

type GaTracker = { get: (arg: string) => string };

export const initGoogleTracker = async (ga: (arg: (tracker: GaTracker) => void) => void) => {
  try {
    const result = await timeout<string>(
      new Promise((resolve) => {
        ga((tracker: GaTracker) => {
          resolve(tracker?.get(LINKER_PARAM) ?? ``);
        });
      }),
      GET_GA_TRACKER_LINKER_PARAM_TIMEOUT
    );
    return result;
  } catch {
    return ``;
  }
};

/**
 * To allow cross-domain sessions in fullStory, this reads the fullStory
 * cookie from the URL and sets it in localStorage before starting fullStory
 */
export const setFullStorySession = () => {
  const params = new URL(window.location.href).searchParams;
  const fsParam = params.get(FULLSTORY_PARAM);
  if (fsParam) {
    const fsSessionId = decodeURIComponent(fsParam);
    localStorage.setItem('_fs_uid', fsSessionId);
  }
};

export const init = async (config: IAnalyticsConfig): Promise<AnalyticsInstance> => {
  setFullStorySession();
  const plugins = [];
  if (config.fullStory.isEnabled) {
    plugins.push(
      fullStoryPlugin({
        org: config.fullStory.organisationId
      })
    );
  }

  if (config.googleAnalytics.isEnabled) {
    plugins.push(
      googleAnalytics({
        trackingId: config.googleAnalytics.trackingId,
        debug: config.googleAnalytics.debug
      })
    );
  }

  if (config.mParticle.isEnabled) {
    plugins.push(
      mParticlePlugin(
        config.mParticle.apiKey,
        {
          ...config.mParticle
        },
        config.mParticle.enabledPlugins
      )
    );
  }

  analytics = Analytics({
    app: `web`,
    version: `1.0`,
    debug: config.debug,
    plugins
  });

  return new Promise((resolve) => {
    // After GA starts, get trackerParam and store it
    analytics.ready(async (payload) => {
      try {
        const googlePlugin = payload.plugins['google-analytics'];
        if (config.googleAnalytics.isEnabled && googlePlugin && googlePlugin.loaded()) {
          const ga = (window as any).ga;
          ga(`require`, `linker`);
          ga(`linker:autoLink`, config.googleAnalytics.autoLinkDomains);
          googleLinkerParam = await initGoogleTracker(ga);
        }
        resolve(analytics);
      } catch {
        resolve(analytics);
      }
    });
  });
};

/**
 * Add Google Analytics Linker Tag before accessing external pages
 * https://developers.google.com/analytics/devguides/collection/analyticsjs/linker
 * @param outboundUrl External URL
 */
export const outboundExternalLink = (outboundUrl: string): string => {
  if (!googleLinkerParam) {
    // GA tracker is disabled in testMode
    window.location.href = outboundUrl;
    return outboundUrl;
  }
  const queryJoiner = outboundUrl.includes('?') ? '&' : '?';
  const url = `${outboundUrl}${queryJoiner}${googleLinkerParam}`;
  window.location.href = url;
  return url;
};

export const identifyUser = async (crc: string, userAttributes: UserAttributes) => {
  crc = crc.toString();
  return analytics.identify(crc, userAttributes);
};

export const logout = async () => {
  return analytics.reset();
};

export const pageView = async (page: PageView, attributes?: EventAttributes, requireLoggedIn?: boolean) => {
  return analytics.page({ ...page }, { attributes, requireLoggedIn });
};

export const trackEvent = async (eventName: string, payload?: EventAttributes, options?: EventOptions) => {
  return analytics.track(eventName, payload, options);
};
